import { atom, type WritableAtom } from 'nanostores';
import { userStore } from '~/lib/stores/user';
import { db, getAll, setProjectDescription, setTechStack } from '~/lib/persistence';
import {
  Free_Git_Repo_Limit,
  Pro_Git_Repo_Limit,
  Free_Total_Repos_Limit,
  Pro_Total_Repos_Limit,
  Index_DB_quota,
} from '~/utils/constants';
import type { ChatHistoryItem } from '~/services/interfaceService';

export interface StorageStatusState {
  isLoading: boolean;
  error: Error | null;
  allProjects: ChatHistoryItem[];
  githubProjects: ChatHistoryItem[];
  otherProjects: ChatHistoryItem[];
  githubRepoUsagePercent: number;
  totalRepoUsagePercent: number;
  showStorageWarning: boolean;
  githubRepoLimit: number;
  totalRepoLimit: number;
  currentGithubRepoCount: number;
  currentTotalRepoCount: number;
  indexDBUsagePercent: number;
  indexDBUsageMB: number;
}

const initialStorageStatusState: StorageStatusState = {
  isLoading: true,
  error: null,
  allProjects: [],
  githubProjects: [],
  otherProjects: [],
  githubRepoUsagePercent: 0,
  totalRepoUsagePercent: 0,
  showStorageWarning: false,
  githubRepoLimit: Free_Git_Repo_Limit,
  totalRepoLimit: Free_Total_Repos_Limit,
  currentGithubRepoCount: 0,
  currentTotalRepoCount: 0,
  indexDBUsagePercent: 0,
  indexDBUsageMB: 0,
};

export const storageStatusStore: WritableAtom<StorageStatusState> = atom(initialStorageStatusState);

export async function projectsFromIDB() {
  const currentStoreState = storageStatusStore.get();

  if (currentStoreState.allProjects.length === 0) {
    storageStatusStore.set({ ...currentStoreState, isLoading: true, error: null });
  }

  try {
    if (!db) {
      console.warn('[storageStatusStore] DB not initialized yet');
      return;
    }

    const projects = await getAll(db);
    const userData = userStore.get();

    const isPro = userData.isSubscribed;
    const currentGitHubLimit = isPro ? Pro_Git_Repo_Limit : Free_Git_Repo_Limit;
    const currentTotalLimit = isPro ? Pro_Total_Repos_Limit : Free_Total_Repos_Limit;
    const currentIndexDBLimit = Index_DB_quota;

    const ghProjects: ChatHistoryItem[] = [];
    const othProjects: ChatHistoryItem[] = [];

    // Filter projects based on source
    for (const project of projects) {
      if (!project.description) {
        await setProjectDescription(db, project.projectId, 'Untitled Project');
        project.description = 'Untitled Project';
      }

      if (!project.techStack) {
        const newTechStack = {
          framework: 'react_tailwind',
          language: 'ts',
          packageManager: 'yarn',
        };
        await setTechStack(db, project.projectId, newTechStack);
        project.techStack = newTechStack;
      }

      if (project.initSource === 'github') {
        ghProjects.push(project);
      } else {
        othProjects.push(project);
      }
    }

    const numGithubRepos = ghProjects.length;
    const numTotalRepos = projects.filter((p) => p && p.description).length;

    // Calculate storage usage
    let indexDBUsageMB = 0;

    try {
      const indexDBUsage = await navigator.storage.estimate();
      indexDBUsageMB = Math.round((indexDBUsage?.usage ? indexDBUsage.usage / 1024 / 1024 : 0) * 100) / 100;
    } catch (e) {
      console.warn('Failed to estimate storage usage', e);
    }

    const ghUsage = currentGitHubLimit > 0 ? (numGithubRepos / currentGitHubLimit) * 100 : numGithubRepos > 0 ? 100 : 0;
    const totalUsage = currentTotalLimit > 0 ? (numTotalRepos / currentTotalLimit) * 100 : numTotalRepos > 0 ? 100 : 0;
    const indexDBUsagePercent = Math.round((indexDBUsageMB / currentIndexDBLimit) * 100);

    console.log('[storageStatusStore] Loaded from IDB:', projects.length, 'projects');

    storageStatusStore.set({
      ...currentStoreState,
      allProjects: projects,
      githubProjects: ghProjects,
      otherProjects: othProjects,
      githubRepoLimit: currentGitHubLimit,
      totalRepoLimit: currentTotalLimit,
      currentGithubRepoCount: numGithubRepos,
      currentTotalRepoCount: numTotalRepos,
      githubRepoUsagePercent: ghUsage,
      totalRepoUsagePercent: totalUsage,
      showStorageWarning: ghUsage >= 90 || totalUsage >= 90 || indexDBUsagePercent >= 90,
      isLoading: false,
      error: null,
      indexDBUsagePercent,
      indexDBUsageMB,
    });
  } catch (e) {
    console.error('[storageStatusStore] Error loading projects from IDB:', e);
    storageStatusStore.set({
      ...currentStoreState,
      isLoading: false,
      error: e instanceof Error ? e : new Error('Failed to load projects'),
    });
  }
}

// cloudRevert (burst)
/**
 * Updates derived state (counts, percentages, limits) based on all projects and user data.
 */
// async function updateDerivedStorageState(projects: ChatHistoryItem[], userData: UserData) {
//   const currentState = storageStatusStore.get();
//   const isPro = userData.isSubscribed;
//   const currentGitHubLimit = isPro ? Pro_Git_Repo_Limit : Free_Git_Repo_Limit;
//   const currentTotalLimit = isPro ? Pro_Total_Repos_Limit : Free_Total_Repos_Limit;
//   const currentIndexDBLimit = Index_DB_quota;
//   const ghProjects: ChatHistoryItem[] = [];
//   const othProjects: ChatHistoryItem[] = [];

//   const finalProjects: ChatHistoryItem[] = [];

//   for (const project of projects) {
//     // map idb projects with the cloud projects
//     if (db) {
//       const idbProjects = await getAll(db);
//       const matchingProject = idbProjects.find((idbProject) => idbProject.projectId === project.projectId);

//       if (matchingProject) {
//         finalProjects.push(project);
//       }
//     }

//     if (project.projectName) {
//       if (project.initSource === 'github') {
//         ghProjects.push(project);
//       } else {
//         othProjects.push(project);
//       }
//     }
//   }

//   const numGithubRepos = ghProjects.length;
//   const numTotalRepos = projects.filter((p) => p && p.projectName).length;
//   const indexDBUsage = await navigator.storage.estimate();
//   const indexDBUsageMB = Math.round((indexDBUsage?.usage ? indexDBUsage.usage / 1024 / 1024 : 0) * 100) / 100;

//   const ghUsage = currentGitHubLimit > 0 ? (numGithubRepos / currentGitHubLimit) * 100 : numGithubRepos > 0 ? 100 : 0;
//   const totalUsage = currentTotalLimit > 0 ? (numTotalRepos / currentTotalLimit) * 100 : numTotalRepos > 0 ? 100 : 0;
//   const indexDBUsagePercent = Math.round((indexDBUsageMB / currentIndexDBLimit) * 100);
//   console.log('[storageStatusStore] IndexDB usage:', indexDBUsageMB, 'MB', indexDBUsagePercent, '%');

//   console.log('[finalProjects] Final projects:', finalProjects);

//   storageStatusStore.set({
//     ...currentState,
//     allProjects: finalProjects,
//     githubProjects: ghProjects,
//     otherProjects: othProjects,
//     githubRepoLimit: currentGitHubLimit,
//     totalRepoLimit: currentTotalLimit,
//     currentGithubRepoCount: numGithubRepos,
//     currentTotalRepoCount: numTotalRepos,
//     githubRepoUsagePercent: ghUsage,
//     totalRepoUsagePercent: totalUsage,
//     showStorageWarning: ghUsage >= 90 || totalUsage >= 90 || indexDBUsagePercent >= 90,
//     isLoading: false,
//     error: null,
//     indexDBUsagePercent,
//     indexDBUsageMB,
//   });
// }

// cloudRevert (burst)
/**
 * Fetches all projects from the database and processes them to update the storage status.
 */
// export async function fetchAndProcessProjects(projects: ChatHistoryItem[]) {
//   const currentStoreState = storageStatusStore.get();
//   storageStatusStore.set({ ...currentStoreState, isLoading: true, error: null });
//   console.log('[fetchAndProcessProjects] Fetching projects...:', projects);

//   const userData = userStore.get();

//   try {
//     await updateDerivedStorageState(projects, userData);
//     console.log('[upload] Projects merged (cloud mapped + unmatched locals) and processed.');
//   } catch (e) {
//     console.error('[upload] Error fetching projects:', e);

//     const currentErrorState = storageStatusStore.get();
//     storageStatusStore.set({
//       ...currentErrorState,
//       isLoading: false,
//       error: e instanceof Error ? e : new Error('Failed to fetch projects'),
//       allProjects: [], // Clear projects on error
//       githubProjects: [],
//       otherProjects: [],
//       currentGithubRepoCount: 0,
//       currentTotalRepoCount: 0,
//       githubRepoUsagePercent: 0,
//       totalRepoUsagePercent: 0,
//       showStorageWarning: false,
//     });
//   }
// }

// Listen to userStore changes to update limits if subscription status changes
userStore.subscribe(async (userData) => {
  const { allProjects, isLoading } = storageStatusStore.get();

  if (allProjects.length > 0 || !isLoading) {
    console.log('[storageStatusStore] User subscription changed, re-calculating storage limits and usage.');
    await projectsFromIDB();
  }
});
